import axios from 'axios'

const state = {
  user: null,
  accessToken: null,
  refreshToken: null
}

const mutations = {
  SET_USER(state, user) {
    state.user = user
  },

  SET_ACCESS_TOKEN(state, token) {
    state.accessToken = token
  },

  SET_REFRESH_TOKEN(state, token) {
    state.refreshToken = token
  },

  CLEAR_AUTH(state) {
    state.user = null
    state.accessToken = null
    state.refreshToken = null
  }
}

const actions = {
  async login({ commit, dispatch }, credentials) {
    try {
      dispatch('setLoading', true, { root: true })

      const response = await axios.post('/auth/login', credentials)
      const { access_token, refresh_token, user } = response.data

      commit('SET_USER', user)
      commit('SET_ACCESS_TOKEN', access_token)
      commit('SET_REFRESH_TOKEN', refresh_token)

      // Store in localStorage
      localStorage.setItem('access_token', access_token)
      localStorage.setItem('refresh_token', refresh_token)
      localStorage.setItem('user', JSON.stringify(user))

      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

      return { success: true, user }
    } catch (error) {
      const errorMessage = error.response?.data?.message || '登录失败'
      dispatch('showNotification', {
        type: 'error',
        title: '登录失败',
        message: errorMessage
      }, { root: true })
      throw error
    } finally {
      dispatch('setLoading', false, { root: true })
    }
  },

  async logout({ commit, dispatch, state }) {
    try {
      // 调用后端接口使 access token 和 refresh token 一起失效（加入黑名单）
      const refreshToken = state.refreshToken || localStorage.getItem('refresh_token')
      await axios.post('/auth/logout', refreshToken ? { refresh_token: refreshToken } : {})
    } catch (error) {
      // 即使后端调用失败，也要继续清除本地状态
      // 静默处理错误，继续执行登出逻辑
    }

    commit('CLEAR_AUTH')

    // Clear localStorage
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')

    delete axios.defaults.headers.common['Authorization']

    dispatch('showNotification', {
      type: 'success',
      title: '退出登录',
      message: '您已成功退出登录'
    }, { root: true })
  },

  async refreshToken({ commit, state }) {
    try {
      const response = await axios.post('/auth/refresh', {
        refresh_token: state.refreshToken
      })

      const { access_token } = response.data
      commit('SET_ACCESS_TOKEN', access_token)

      localStorage.setItem('access_token', access_token)
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

      return access_token
    } catch (error) {
      // If refresh fails, logout user
      commit('CLEAR_AUTH')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
      delete axios.defaults.headers.common['Authorization']
      window.location.href = '/login'
      throw error
    }
  },

  async updateProfile({ commit }, profileData) {
    const response = await axios.put('/auth/profile', profileData)
    const updatedUser = response.data.user

    commit('SET_USER', updatedUser)
    localStorage.setItem('user', JSON.stringify(updatedUser))

    return updatedUser
  },

  loginSuccess({ commit }, { access_token, user }) {
    commit('SET_USER', user)
    commit('SET_ACCESS_TOKEN', access_token)

    // Store in localStorage
    localStorage.setItem('access_token', access_token)
    localStorage.setItem('user', JSON.stringify(user))

    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
  }
}

const getters = {
  isAuthenticated: state => !!state.accessToken && !!state.user,
  currentUser: state => state.user,
  userRole: state => state.user?.role?.name,
  isAdmin: state => Number(state.user?.role_id) === 3,
  isSales: state => Number(state.user?.role_id) === 1,
  isAssistant: state => Number(state.user?.role_id) === 2
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}